import React from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import {
  PlayCircleIcon,
  StopCircleIcon,
  EditIcon,
  DuplicateIcon,
} from '@vlabs/icons';
import { permissions } from '@vlabs/shared/config';
import { CopyButton, Control, Tooltip } from '@vlabs/uikit';
import cn from 'classnames';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import { STREAM_STATUSES } from '@vlabs/pages/video-analytics/constants';

import { selectAvailableAnalyticsList } from '../../../selectors';
import { AnalyticStatus } from '../AnalyticStatus';
import st from './Cell.module.sass';

function StartStreamCell({
  can,
  value = {
    onReset: () => { },
    status: null,
    stream_id: '',
  },
}) {
  const { t } = useTranslation();

  if (!can([permissions.videoanalytics.modification])) return null;

  const isDisabled = value.status !== 5;
  return (
    <Tooltip
      tooltipContent={isDisabled ? t('videoAnalytics:tooltips.resume') : null}
      tooltipProps={{ place: 'bottom', offset: 14, style: { minWidth: 200 } }}
    >
      <Control.IconButton
        data-testid="play-stream-button"
        disabled={isDisabled}
        icon={<PlayCircleIcon data-testid="icon-play" />}
        kind="secondary"
        onClick={() => {
          apiContainer.lunaClient.streams.patch(value.stream_id, 'resume');
          value.onReset();
        }}
        shape="round"
        size="medium"
        title={t('videoAnalytics:buttons.resume')}
        type="button"
        variant="flat"
      />
    </Tooltip>
  );
}

StartStreamCell.propTypes = {
  can: PropTypes.func.isRequired,
  value: PropTypes.shape({
    onReset: PropTypes.func,
    status: PropTypes.number,
    stream_id: PropTypes.string,
  }),
};

const $StartStreamCell = connect((state) => ({
  can: viewerCan(state),
}))(StartStreamCell);

export { $StartStreamCell as StartStreamCell };

function StopStreamCell({
  can,
  value = {
    onReset: () => { },
    status: null,
    stream_id: '',
  },
}) {
  const { t } = useTranslation();

  if (!can([permissions.videoanalytics.modification])) return null;

  const isDisabled = value.status !== 1;
  return (
    <Tooltip
      tooltipContent={isDisabled ? t('videoAnalytics:tooltips.stop') : null}
      tooltipProps={{ place: 'bottom', offset: 1, style: { minWidth: 200 } }}
    >
      <Control.IconButton
        data-testid="stop-stream-button"
        disabled={isDisabled}
        icon={<StopCircleIcon data-testid="icon-stop" />}
        kind="secondary"
        onClick={() => {
          apiContainer.lunaClient.streams.patch(value.stream_id, 'stop');
          value.onReset();
        }}
        shape="round"
        size="medium"
        title={t('videoAnalytics:buttons.stop')}
        type="button"
        variant="flat"
      />
    </Tooltip>
  );
}

StopStreamCell.propTypes = {
  can: PropTypes.func.isRequired,
  value: PropTypes.shape({
    onReset: PropTypes.func,
    status: PropTypes.number,
    stream_id: PropTypes.string,
  }),
};

const $StopStreamCell = connect((state) => ({
  can: viewerCan(state),
}))(StopStreamCell);

export { $StopStreamCell as StopStreamCell };

function StreamNameCell({ value = '' }) {
  return (
    <div className={st.StreamName}>
      <span key={value}>{value}</span>
    </div>
  );
}

StreamNameCell.propTypes = {
  value: PropTypes.string,
};

export { StreamNameCell };

function StatusLabelCell({ value = null }) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(st.StreamStatusTag, st[`StreamStatusTag__${STREAM_STATUSES[value]?.label}`])}
      data-testid="StreamStatusTag"
    >
      {t(`videoAnalytics:request_status.${STREAM_STATUSES[value]?.label}`, { defaultValue: '' })}
    </div>
  );
}

StatusLabelCell.propTypes = {
  value: PropTypes.number,
};

export { StatusLabelCell };

const ANALYTIC_TITLES = {
  get people_count() {
    return i18n.t('videoAnalytics:event_type.crowd.label');
  },
  get fights_analytics() {
    return i18n.t('videoAnalytics:event_type.fight.label');
  },
  get fire_analytics() {
    return i18n.t('videoAnalytics:event_type.fire.label');
  },
  get weapon_analytics() {
    return i18n.t('videoAnalytics:event_type.weapon.label');
  },
  get facecover_analytics() {
    return i18n.t('videoAnalytics:event_type.facecover.label');
  },
  get bags_analytics() {
    return i18n.t('videoAnalytics:event_type.bag.label');
  },
  get lying_analytics() {
    return i18n.t('videoAnalytics:event_type.lying.label');
  },
  get handsup_analytics() {
    return i18n.t('videoAnalytics:event_type.handsup.label');
  },
  get fall_analytics() {
    return i18n.t('videoAnalytics:event_type.fall.label');
  },
};

function AnalyticNameCell({ value = [] }) {
  if (!value) return null;

  return (
    <React.Fragment>
      {value.map((el) => (
        <AnalyticStatus
          name={ANALYTIC_TITLES[el.analytic_name] ?? el.analytic_name}
          status={STREAM_STATUSES[el.status]?.label || 'unknown'}
        />
      ))}
    </React.Fragment>
  );
}

AnalyticNameCell.propTypes = {
  value: PropTypes.array,
};

export { AnalyticNameCell };

function StreamIdCell({ value = '' }) {
  const { t } = useTranslation();
  return (
    <CopyButton
      className={st.StreamsIds}
      data-testid={`${value}--copy`}
      label={value}
      title={t('copy_action')}
      value={value}
    />
  );
}

StreamIdCell.propTypes = {
  value: PropTypes.string,
};

export { StreamIdCell };

function StreamGroupsCell({ value = [] }) {
  return (
    <div className={st.StreamGroups}>
      {value.map((el) => (
        <span key={el}>{el}</span>
      ))}
    </div>
  );
}

StreamGroupsCell.propTypes = {
  value: PropTypes.array,
};

export { StreamGroupsCell };

function StreamDescriptionCell({ value = '' }) {
  return (
    <div className={st.StreamDescription}>
      <span key={value}>{value}</span>
    </div>
  );
}

StreamDescriptionCell.propTypes = {
  value: PropTypes.string,
};

export { StreamDescriptionCell };

function EditStreamCell({
  can,
  value: { analytics, streamId } = {
    stream_id: '',
    analytics: [],
  },
  availableAnalyticsList = [],
}) {
  const { t } = useTranslation();
  const history = useHistory();

  const streamAnalytics = analytics.map((analytic) => analytic.analytic_name);
  const canEdit = streamAnalytics.length && streamAnalytics.every((analytic) => (
    availableAnalyticsList.includes(analytic)));

  const onEditStream = () => {
    history.push(`/video-analytics/streams/${streamId}/edit/`, { mode: 'edit' });
  };

  if (!can([permissions.videoanalytics.modification])) return null;

  return (
    <Control.IconButton
      data-testid="edit-stream-button"
      disabled={!canEdit}
      icon={<EditIcon data-testid="icon-edit" />}
      kind="secondary"
      onClick={onEditStream}
      shape="round"
      size="medium"
      title={t('uikit:table.actions.edit')}
      type="button"
      variant="flat"
    />
  );
}

EditStreamCell.propTypes = {
  value: PropTypes.shape({
    streamId: PropTypes.string,
    analytics: PropTypes.arrayOf(PropTypes.object),
  }),
  can: PropTypes.func.isRequired,
  availableAnalyticsList: PropTypes.arrayOf(PropTypes.string),
};

const $EditStreamCell = connect((state) => ({
  can: viewerCan(state),
  availableAnalyticsList: selectAvailableAnalyticsList(state),
}))(EditStreamCell);

export { $EditStreamCell as EditStreamCell };

function DuplicateStreamCell({
  can,
  value = '',
}) {
  const { t } = useTranslation();

  if (!can([permissions.videoanalytics.creation])) return null;

  return (
    <Control.IconButton
      data-testid="DuplicateStreamButton"
      disabled
      icon={<DuplicateIcon data-testid="icon-duplicate" />}
      kind="secondary"
      shape="round"
      size="medium"
      // onClick={}
      title={t('uikit:table.actions.edit')}
      type="button"
      variant="flat"
    />
  );
}

DuplicateStreamCell.propTypes = {
  value: PropTypes.string,
  can: PropTypes.func.isRequired,
};

const $DuplicateStreamCell = connect((state) => ({
  can: viewerCan(state),
}))(DuplicateStreamCell);

export { $DuplicateStreamCell as DuplicateStreamCell };
